import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { ChevronDown, Menu, X, Search } from "lucide-react";
import { useDocsDrawer } from "./DocsDrawerContext";
import SearchModal from "./SearchModal";

export default function Navbar() {
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { open: drawerOpen, setOpen: setDrawerOpen } = useDocsDrawer();
  
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = (idx: number) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveDropdown(idx);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
  };

  const isDocs = location.pathname.startsWith("/docs");
  const isPricing = location.pathname === "/pricing";

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((open) => !open);
      } else if (e.key === "Escape") {
        setActiveDropdown(null);
        setIsMobileMenuOpen(false);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Close dropdown on route changes
  useEffect(() => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const developersDropdown = [
    { title: 'Documentation', desc: 'Getting started guide', path: '/docs/overview/getting-started' },
    { title: 'Projects', desc: 'See what you can build', path: '/projects' },
    { title: 'SDKs', desc: 'SDK documentation', path: '/docs/sdks' },
    { title: 'Skills', desc: 'Developer skills', path: '/skills' },
  ];

  const companyDropdown = [
    { title: 'About', desc: 'Our mission and team', path: '/about' },
    { title: 'Feedback', desc: 'Share your thoughts', path: '/feedback' },
    { title: 'FAQ', desc: 'Common questions', path: '/faq' },
    { title: 'Privacy', desc: 'Privacy policy', path: '/privacy' },
  ];

  return (
    <>
      <nav className="flex-none relative z-50 flex items-center justify-between px-2 sm:px-6 pb-3 sm:pb-5">
        
        {/* Logo Section */}
        <div className="flex items-center gap-2 w-auto h-8">
          <Link to="/" className="block w-full h-full flex items-center justify-start gap-2.5">
            <img 
              src="/solarch-logo.svg" 
              alt="Solarch Logo" 
              className="h-full w-auto object-contain" 
            />
            <span className="font-heading font-bold text-xl tracking-wide text-theme">Solarch</span>
          </Link>
        </div>

        {/* Desktop Navigation Links: Developers ▾ | Pricing | Company ▾ */}
        <div className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
          
          {/* Developers Dropdown */}
          <div 
            className="relative"
            onMouseEnter={() => handleMouseEnter(0)}
            onMouseLeave={handleMouseLeave}
          >
            <button 
              aria-haspopup="true"
              aria-expanded={activeDropdown === 0}
              onClick={() => setActiveDropdown(prev => prev === 0 ? null : 0)}
              className={`flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium rounded-full transition-colors ${
                activeDropdown === 0 
                  ? 'text-primary bg-theme-elevated' 
                  : 'text-theme hover:text-primary hover:bg-theme-elevated'
              }`}
            >
              Developers
              <ChevronDown 
                size={16} 
                className={`transition-transform duration-200 ${
                  activeDropdown === 0 ? 'rotate-180 text-primary' : 'text-theme/70'
                }`}
              />
            </button>

            {/* Developers Dropdown Menu with contiguous hover bridge */}
            <div 
              className={`absolute top-full left-1/2 -translate-x-1/2 pt-2 w-[320px] transition-all duration-300 ease-out origin-top z-[100] ${
                activeDropdown === 0 
                  ? 'opacity-100 visible scale-100 translate-y-0 pointer-events-auto' 
                  : 'opacity-0 invisible scale-95 -translate-y-2 pointer-events-none'
              }`}
              onMouseEnter={() => handleMouseEnter(0)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="rounded-3xl p-3 shadow-2xl bg-theme-surface border border-theme flex flex-col">
                {developersDropdown.map((dropItem, dropIdx) => (
                  <Link 
                    key={dropIdx} 
                    to={dropItem.path} 
                    className="flex items-start gap-4 p-3 rounded-2xl hover:bg-theme-elevated transition-colors group/item cursor-pointer"
                    onClick={() => setActiveDropdown(null)}
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="text-theme text-sm font-medium transition-colors group-hover/item:text-primary">
                        {dropItem.title}
                      </span>
                      <span className="text-theme-secondary text-sm transition-colors">
                        {dropItem.desc}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Top-Level Pricing Link */}
          <Link
            to="/pricing"
            className={`px-5 py-2.5 text-sm font-medium rounded-full transition-colors ${
              isPricing
                ? 'text-primary bg-theme-elevated font-semibold'
                : 'text-theme hover:text-primary hover:bg-theme-elevated'
            }`}
          >
            Pricing
          </Link>

          {/* Company Dropdown */}
          <div 
            className="relative"
            onMouseEnter={() => handleMouseEnter(1)}
            onMouseLeave={handleMouseLeave}
          >
            <button 
              aria-haspopup="true"
              aria-expanded={activeDropdown === 1}
              onClick={() => setActiveDropdown(prev => prev === 1 ? null : 1)}
              className={`flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium rounded-full transition-colors ${
                activeDropdown === 1 
                  ? 'text-primary bg-theme-elevated' 
                  : 'text-theme hover:text-primary hover:bg-theme-elevated'
              }`}
            >
              Company
              <ChevronDown 
                size={16} 
                className={`transition-transform duration-200 ${
                  activeDropdown === 1 ? 'rotate-180 text-primary' : 'text-theme/70'
                }`}
              />
            </button>

            {/* Company Dropdown Menu with contiguous hover bridge */}
            <div 
              className={`absolute top-full left-1/2 -translate-x-1/2 pt-2 w-[320px] transition-all duration-300 ease-out origin-top z-[100] ${
                activeDropdown === 1 
                  ? 'opacity-100 visible scale-100 translate-y-0 pointer-events-auto' 
                  : 'opacity-0 invisible scale-95 -translate-y-2 pointer-events-none'
              }`}
              onMouseEnter={() => handleMouseEnter(1)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="rounded-3xl p-3 shadow-2xl bg-theme-surface border border-theme flex flex-col">
                {companyDropdown.map((dropItem, dropIdx) => (
                  <Link 
                    key={dropIdx} 
                    to={dropItem.path} 
                    className="flex items-start gap-4 p-3 rounded-2xl hover:bg-theme-elevated transition-colors group/item cursor-pointer"
                    onClick={() => setActiveDropdown(null)}
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="text-theme text-sm font-medium transition-colors group-hover/item:text-primary">
                        {dropItem.title}
                      </span>
                      <span className="text-theme-secondary text-sm transition-colors">
                        {dropItem.desc}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

        </div>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-6 z-10">
          <div className="flex items-center gap-4">
            {isDocs && (
              <button
                onClick={() => setSearchOpen(true)}
                className="text-theme-secondary hover:text-theme transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <Search size={18} />
              </button>
            )}
            <Link
              to="/waitlist"
              className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white transition-all hover:brightness-110 active:scale-95"
            >
              Join Waitlist
            </Link>
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <button 
          onClick={() => {
            if (isDocs) {
              setDrawerOpen(!drawerOpen);
            } else {
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }
          }}
          className="p-2 rounded-full hover:bg-theme-elevated text-theme-secondary hover:text-theme md:hidden z-20 transition-colors"
        >
          {isDocs ? (
            drawerOpen ? <X size={24} /> : <Menu size={24} />
          ) : (
            isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />
          )}
        </button>

      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        className={`absolute top-20 left-4 right-4 p-6 bg-theme-surface/80 backdrop-blur-2xl border border-theme rounded-[2rem] shadow-2xl md:hidden transition-all duration-300 origin-top z-40 ${
          !isDocs && isMobileMenuOpen 
            ? 'opacity-100 visible scale-100' 
            : 'opacity-0 invisible scale-95 pointer-events-none'
        }`}
      >
        <div className="flex flex-col">
          {/* Developers Section */}
          <div className="py-2 font-bold text-theme-muted text-xs uppercase tracking-wider mt-2 mb-1 pl-2">
            Developers
          </div>
          <div className="flex flex-col pb-2 space-y-1 pl-2">
            {developersDropdown.map((dropItem, dropIdx) => (
              <Link 
                key={dropIdx}
                to={dropItem.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className="py-2 font-medium text-theme hover:text-primary transition-colors"
              >
                {dropItem.title}
              </Link>
            ))}
          </div>

          {/* Pricing Section */}
          <div className="py-2 font-bold text-theme-muted text-xs uppercase tracking-wider mt-2 mb-1 pl-2">
            Pricing
          </div>
          <div className="flex flex-col pb-2 space-y-1 pl-2">
            <Link 
              to="/pricing"
              onClick={() => setIsMobileMenuOpen(false)}
              className="py-2 font-medium text-theme hover:text-primary transition-colors"
            >
              Pricing Overview
            </Link>
          </div>

          {/* Company Section */}
          <div className="py-2 font-bold text-theme-muted text-xs uppercase tracking-wider mt-2 mb-1 pl-2">
            Company
          </div>
          <div className="flex flex-col pb-2 space-y-1 pl-2">
            {companyDropdown.map((dropItem, dropIdx) => (
              <Link 
                key={dropIdx}
                to={dropItem.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className="py-2 font-medium text-theme hover:text-primary transition-colors"
              >
                {dropItem.title}
              </Link>
            ))}
          </div>

          <div className="h-px bg-theme-hover my-4" />

          <div className="flex flex-col gap-4">
            {isDocs && (
              <button
                onClick={() => {
                  setSearchOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                className="py-2 font-medium text-theme hover:text-primary transition-colors flex items-center gap-2"
              >
                <Search size={18} /> Search
              </button>
            )}
            <Link
              to="/waitlist"
              onClick={() => setIsMobileMenuOpen(false)}
              className="py-3 mt-2 font-medium text-center text-white bg-primary rounded-xl transition-all hover:brightness-110 active:scale-95"
            >
              Join Waitlist
            </Link>
          </div>
        </div>
      </div>

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
